import { ReactNode } from 'react';
import { alpha } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { IDECON_WEBSITE_URL } from '../constants';
import logoSrc from 'assets/images/idecon-header-logo.png';
import { ColorModeIconDropdown, LanguagePicker } from 'components';
import { Box, ButtonBase, Container, Paper, Stack, Typography } from '@mui/material';

type SurveyShellProps = {
  children: ReactNode;
};

const SurveyShell = ({ children }: SurveyShellProps) => (
  <Box
    sx={(theme) => ({
      position: 'relative',
      isolation: 'isolate',
      display: 'flex',
      minHeight: '100vh',
      overflow: 'hidden',
      backgroundColor: theme.palette.background.default,
      backgroundImage: `
        radial-gradient(circle at 12% 8%, ${alpha(theme.palette.primary.main, 0.16)}, transparent 34%),
        radial-gradient(circle at 88% 88%, ${alpha(theme.palette.info.main, 0.12)}, transparent 32%)
      `,
      '&::before, &::after': {
        position: 'absolute',
        zIndex: -1,
        borderRadius: '50%',
        content: '""',
        filter: 'blur(2px)',
        pointerEvents: 'none',
      },
      '&::before': {
        top: { xs: -100, md: -160 },
        right: { xs: -120, md: -80 },
        width: { xs: 240, md: 360 },
        height: { xs: 240, md: 360 },
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
      },
      '&::after': {
        bottom: { xs: -130, md: -200 },
        left: { xs: -120, md: -140 },
        width: { xs: 260, md: 420 },
        height: { xs: 260, md: 420 },
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      '@supports (min-height: 100dvh)': {
        minHeight: '100dvh',
      },
    })}>
    <Container
      maxWidth="md"
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
      }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 2, sm: 3 },
        }}>
        <ButtonBase
          component="a"
          href={IDECON_WEBSITE_URL}
          aria-label="Idecon"
          sx={{ borderRadius: 1.5, p: 0.75, ml: -0.75 }}>
          <Box component="img" src={logoSrc} alt="Idecon" sx={{ display: 'block', width: { xs: 96, sm: 112 } }} />
        </ButtonBase>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <LanguagePicker />
          <ColorModeIconDropdown />
        </Stack>
      </Box>

      <Paper
        component="main"
        elevation={0}
        sx={(theme) => ({
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: { xs: 2.5, sm: 3.5 },
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.92 : 0.88),
          boxShadow:
            theme.palette.mode === 'light' ? '0 24px 70px rgba(16, 24, 40, 0.10)' : '0 24px 70px rgba(0, 0, 0, 0.30)',
          backdropFilter: 'blur(16px)',
        })}>
        {children}
      </Paper>

      <Typography
        component="footer"
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{ mt: 'auto', pt: { xs: 2.5, sm: 3.5 } }}>
        <FormattedMessage
          id="ticketSurvey.copyright"
          defaultMessage="© {year} Idecon Danışmanlık Hizmetleri A.Ş."
          values={{ year: new Date().getFullYear() }}
        />
      </Typography>
    </Container>
  </Box>
);

export default SurveyShell;
